/**
 * New Customer cards
 *
 * Ports the two customer cards from the Command Center onto the admin dashboard.
 *
 * The Command Center builds its snapshot itself — one-off prod ECS task, plus
 * CloudWatch, Cognito and LogRocket — which a browser app can't do. It doesn't
 * need to: the signup-profile worker already writes the same enrichment into
 * customer_profiles about an hour after signup, so both cards read one endpoint
 * and aggregate client-side exactly like the originals do.
 *
 * @module pages/components
 */

import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip, Skeleton } from '@mui/material';
import { PieChart as OverviewIcon, PersonAdd as SignupIcon } from '@mui/icons-material';

import authInstance from '../../services/httpClient';
import { useAdminTheme } from '../../admin-theme';
import { FONT_DATA, FONT_DISPLAY } from '../../theme/grounds';
import Logger from '../../utils/Logger';

// ============================================================================
// Types
// ============================================================================

export interface RecentSignup {
  id: string;
  name: string;
  email?: string | null;
  createdAt: string;
  location: string | null;
  platform: string | null;
  device: string | null;
  source: string | null;
  // Full-profile fields, read by the New Customers page (the dashboard cards
  // only use the summary above). Absent from older backends, hence optional.
  likelySource?: string | null;
  sourcePage?: string | null;
  searchTerm?: string | null;
  geoNetwork?: string | null;
  firstAddress?: string | null;
  inSession?: string | null;
  priorCustomer: boolean;
  phoneVerified: boolean;
  addressAdded: boolean;
  profileStatus: string;
}

export interface RecentSignupsResponse {
  signups: RecentSignup[];
  total: number;
  enriched: number;
  generatedAt: string;
}

// ============================================================================
// Shared fetch — both cards render from one request
// ============================================================================

/** enabled = false skips the request (a staff dashboard without the New Customers tick). */
export function useRecentSignups(enabled = true) {
  const [data, setData] = useState<RecentSignupsResponse | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;
    setLoading(true);
    let alive = true;
    authInstance
      .get('admin/users/customers/recent-signups')
      .then(({ data: body }) => {
        if (alive) setData(body);
      })
      .catch((err: unknown) => {
        Logger.error('Failed to fetch recent signups:', err);
        if (alive) setError('Could not load new customers.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [enabled]);

  return { data, loading, error };
}

// ============================================================================
// Helpers (behaviour carried over from the Command Center cards)
// ============================================================================

/** City = the part after the last comma of "Suburb, City" (else the whole). */
function cityOf(location: string | null): string | null {
  if (!location) return null;
  const parts = location.split(',').map((s) => s.trim()).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : null;
}

/** Tally non-null values, most frequent first. */
function topN(items: (string | null)[], n: number): Array<[string, number]> {
  const counts = new Map<string, number>();
  for (const item of items) {
    if (item) counts.set(item, (counts.get(item) ?? 0) + 1);
  }
  // Array.from rather than spread — the app targets ES5 downlevel iteration.
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, n);
}

export function ago(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${Math.max(mins, 1)}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return mins % 60 ? `${hours}h ${mins % 60}m ago` : `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// ============================================================================
// New Customer Info — the latest few signups
// ============================================================================

export interface NewCustomerInfoProps {
  data: RecentSignupsResponse | null;
  loading: boolean;
  error: string | null;
  /** How many rows to show. */
  limit?: number;
}

export function NewCustomerInfo({ data, loading, error, limit = 5 }: NewCustomerInfoProps): React.ReactElement {
  const { colors } = useAdminTheme();
  const latest = (data?.signups ?? []).slice(0, limit);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {Array.from({ length: limit }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={34} sx={{ bgcolor: colors.bgElevated }} />
        ))}
      </Box>
    );
  }

  if (error || latest.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: colors.textMuted }}>
        {error ?? 'No signups yet.'}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {latest.map((customer, index) => (
        <Box
          key={customer.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: '9px',
            borderTop: index === 0 ? 'none' : `1px solid ${colors.border}`,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.84rem',
                color: colors.textPrimary,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {customer.name}
            </Typography>
            <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.6875rem', color: colors.textMuted, mt: '1px' }}>
              {customer.location ?? (customer.profileStatus === 'pending' ? 'still resolving' : 'location unknown')}
            </Typography>
          </Box>
          {customer.priorCustomer && (
            <Chip
              label="Prior"
              size="small"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                fontWeight: 700,
                color: colors.warning,
                bgcolor: 'transparent',
                border: `1px solid ${colors.warning}`,
              }}
            />
          )}
          <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.72rem', color: colors.textSecondary, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
            {ago(customer.createdAt)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// ============================================================================
// New Customer Overview — the same snapshot, aggregated
// ============================================================================

export interface NewCustomerOverviewProps {
  data: RecentSignupsResponse | null;
  loading: boolean;
  error: string | null;
}

export function NewCustomerOverview({ data, loading, error }: NewCustomerOverviewProps): React.ReactElement {
  const { colors } = useAdminTheme();
  const signups = data?.signups ?? [];
  const total = signups.length;

  if (loading) {
    return <Skeleton variant="rounded" height={190} sx={{ bgcolor: colors.bgElevated }} />;
  }

  if (error || total === 0) {
    return (
      <Typography variant="body2" sx={{ color: colors.textMuted }}>
        {error ?? 'No signups yet.'}
      </Typography>
    );
  }

  const android = signups.filter((s) => /android/i.test(s.platform || '')).length;
  const ios = signups.filter((s) => /ios|ipad|iphone/i.test(s.platform || '')).length;
  const cities = topN(signups.map((s) => cityOf(s.location)), 3);
  const sources = topN(signups.map((s) => s.source), 3);
  const prior = signups.filter((s) => s.priorCustomer).length;
  const pending = signups.filter((s) => s.profileStatus === 'pending').length;

  return (
    <Box>
      <Typography sx={{ fontSize: '0.84rem', color: colors.textSecondary, mb: '14px' }}>
        Of your last <strong style={{ color: colors.textPrimary }}>{total}</strong> signup
        {total === 1 ? '' : 's'}:
      </Typography>

      {/* Two tiles, boxed. The prior-customer count is the closing line. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
        {[
          { label: 'Android', value: android },
          { label: 'iOS', value: ios },
        ].map((stat) => (
          <Box
            key={stat.label}
            sx={{ bgcolor: colors.bgElevated, border: `1px solid ${colors.border}`, borderRadius: '10px', p: '12px 14px' }}
          >
            <Typography
              sx={{ fontFamily: FONT_DISPLAY, fontStretch: '125%', fontWeight: 800, fontSize: '1.75rem', lineHeight: 1, letterSpacing: '-0.035em', color: colors.textPrimary, fontVariantNumeric: 'tabular-nums' }}
            >
              {stat.value}
            </Typography>
            <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.6875rem', color: colors.textMuted, mt: '5px' }}>
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <OverviewHead colors={colors}>Top cities</OverviewHead>
      {cities.length === 0 ? (
        <Typography sx={{ fontSize: '0.84rem', color: colors.textMuted, fontStyle: 'italic' }}>locations still resolving</Typography>
      ) : (
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 650, color: colors.textPrimary }}>
          {cities.map(([name, count]) => `${name} ${count}`).join(' · ')}
        </Typography>
      )}

      <OverviewHead colors={colors}>Top sources</OverviewHead>
      <SubList rows={sources} emptyNote="sources not yet resolved" colors={colors} />

      <Typography sx={{ mt: '14px', pt: '12px', borderTop: `1px solid ${colors.border}`, fontSize: '0.875rem', color: colors.textPrimary }}>
        {prior} of {total} were prior Deeliver customers.
      </Typography>

      {pending > 0 && (
        <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.6875rem', color: colors.textMuted, display: 'block', mt: 1.5 }}>
          {pending} profile{pending === 1 ? '' : 's'} still building — enrichment lands about an hour after signup.
        </Typography>
      )}
    </Box>
  );
}

/** Mono eyebrow between the overview's sections — the prototype's `.ov-head`. */
function OverviewHead({ children, colors }: { children: React.ReactNode; colors: ReturnType<typeof useAdminTheme>['colors'] }): React.ReactElement {
  return (
    <Typography
      sx={{
        fontFamily: FONT_DATA,
        fontSize: '0.66rem',
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: colors.textMuted,
        mt: '14px',
        mb: '5px',
      }}
    >
      {children}
    </Typography>
  );
}

interface SubListProps {
  rows: Array<[string, number]>;
  emptyNote: string;
  colors: ReturnType<typeof useAdminTheme>['colors'];
}

function SubList({ rows, emptyNote, colors }: SubListProps): React.ReactElement {
  if (rows.length === 0) {
    return (
      <Typography sx={{ fontSize: '0.84rem', color: colors.textMuted, fontStyle: 'italic' }}>
        {emptyNote}
      </Typography>
    );
  }
  return (
    <Box>
      {rows.map(([name, count], i) => (
        <Box
          key={name}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: '4px', borderTop: i === 0 ? 'none' : `1px solid ${colors.border}` }}
        >
          <Typography
            sx={{
              fontSize: '0.84rem',
              color: colors.textSecondary,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={name}
          >
            {name}
          </Typography>
          <Typography
            sx={{ fontFamily: FONT_DATA, fontSize: '0.78rem', color: colors.textPrimary, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
          >
            {count}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export const NEW_CUSTOMER_ICONS = { overview: <OverviewIcon />, info: <SignupIcon /> };
